import { Copy } from 'lucide-react';
import { toast } from 'sonner';

import { t } from '../translations.js';
import { Button } from '../ui/button.js';

export type DebugBlockProps = {
  /** Stable, non-localised error code. */
  code: string;
  /** Triage reference: the backend `requestId`, a Sentry `eventId` (crash), or
   *  a client UUID matching the Sentry log — whichever the surface resolved. */
  reference: string;
  /** When the error surfaced. */
  timestamp: Date;
};

/**
 * Small technical block for unexpected-error screens — an error code, a
 * reference ID and a timestamp, with a copy button. Pure presentation: the
 * caller supplies `reference` / `timestamp` (from `useErrorReference`, or
 * directly from a Sentry `eventId`). Rendered in all environments.
 */
export function DebugBlock({ code, reference, timestamp }: DebugBlockProps) {
  const formattedTime = timestamp.toLocaleString('nl-NL');

  const handleCopy = async () => {
    const text = `${t.debugBlock.code}: ${code} | ${t.debugBlock.reference}: ${reference} | ${formattedTime}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t.debugBlock.copied);
    } catch {
      // Clipboard unavailable (insecure context / permissions) — the values
      // stay visible on screen, so there is nothing to recover from.
    }
  };

  return (
    <div
      className="space-y-1 rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground"
      data-testid="debug-block"
    >
      <div className="flex items-center justify-between gap-2">
        <span>
          {t.debugBlock.code}: <span className="font-mono">{code}</span>
        </span>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 px-2"
          data-testid="debug-block-copy-btn"
          aria-label={t.debugBlock.copy}
          title={t.debugBlock.copy}
          onClick={() => {
            void handleCopy();
          }}
        >
          <Copy className="h-3 w-3" aria-hidden="true" />
        </Button>
      </div>
      <div data-testid="debug-block-reference">
        {t.debugBlock.reference}: <span className="font-mono">{reference}</span>
      </div>
      <div>{formattedTime}</div>
    </div>
  );
}
